import { installConsoleCapture } from "./utils/console-capture";
installConsoleCapture();

// Install stale-chunk recovery before any dynamic imports fire. After a deploy,
// the old SPA tab can't load new chunk hashes — auto-reload once to recover.
import { installChunkErrorRecovery } from "./utils/chunk-error-recovery";
installChunkErrorRecovery();

// Self-hosted webfonts. NOT linked from fonts.googleapis.com — that would
// transmit visitor IPs to Google, which is a GDPR problem for an EU product.
// Literata = display serif, Inter = UI sans (see styles/base/typography).
import "@fontsource-variable/literata";
import "@fontsource-variable/inter";
import "./styles/global-reset.scss";
import { bootstrapTheme } from "./hooks/useTheme";
// Apply theme to <html> before React mounts so first paint matches.
bootstrapTheme();
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider, Helmet } from "react-helmet-async";

import store from "./store";
import routes from "./routes";
import ErrorBoundry from "./components/common/error-boundry";
import i18n from "./translations/i18n";
import { initAnalyticsFromStoredConsent } from "./utils/analytics-consent";

// Defer monitoring initialization (PostHog + Sentry + Hotjar, ~258KB)
// to after the initial render for faster first paint.
import { initMonitoring } from "./utils/init-monitoring";
if (typeof requestIdleCallback === "function") {
  requestIdleCallback(() => { initMonitoring(); });
} else {
  setTimeout(() => { initMonitoring(); }, 1);
}

// GA4 Analytics — only loads if the visitor previously accepted the cookie
// consent banner (<CookieConsentBanner />, rendered in AppContainer). Never
// loads unconditionally per GDPR/TKG §165.
initAnalyticsFromStoredConsent();

const router = createBrowserRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <HelmetProvider>
    {/* Default <meta name="description"> owned by Helmet so per-page SEOHead
        instances can OVERRIDE it (Helmet dedupes meta tags by name). The static
        copy in index.html was removed to avoid two competing description tags. */}
    <Helmet>
      <meta
        name="description"
        content="Learn effectively with interactive exercises, structured courses, and personalized learning paths."
      />
    </Helmet>
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <ErrorBoundry>
          <RouterProvider
            router={router}
            future={{
              v7_startTransition: true,
            }}
          />
        </ErrorBoundry>
      </Provider>
    </I18nextProvider>
  </HelmetProvider>
);

// Remove the instant-paint loader once React has mounted.
// Delay one animation frame so the first React render has painted.
requestAnimationFrame(() => {
  document.getElementById("initial-loader")?.remove();
});
